/**
 * auto-artifacts: Artifacts-tab rendering for galleries, A/B comparisons and Claude artifacts.
 * Index entries become transcript artifact items so the tab shares the transcript's collection
 * view, lightbox and Claude artifact card; media still decrypts only on this device through the
 * endpoint renderer. Every version of one claude.ai page is one group: latest first, the rest
 * behind "Earlier versions".
 */
import {
  type ArtifactItem,
  ArtifactRun,
  SingleArtifact,
  ViewerContext,
  type ViewerContextValue,
} from '@viko/transcript-ui'
import { useMemo } from 'react'
import { type ArtifactViewerAccess, EndpointArtifact } from '../lib/artifact-viewer.js'
import type { Artifact } from '../lib/types.js'

export function isAutoArtifact(a: Artifact): boolean {
  return !!(a.collection || a.external || a.bundle)
}

export function toArtifactItem(a: Artifact): ArtifactItem {
  return {
    type: 'artifact',
    id: a.id,
    seq: 0,
    ts: a.createdAt,
    state: 'ok',
    artifactId: a.id,
    name: a.title ?? a.name ?? a.id,
    ...(a.mime ? { mime: a.mime } : {}),
    ...(a.bytes != null ? { bytes: a.bytes } : {}),
    ...(a.caption ? { caption: a.caption } : {}),
    ...(a.encrypted ? { encrypted: a.encrypted } : {}),
    ...(a.thumbEncrypted ? { thumbEncrypted: a.thumbEncrypted } : {}),
    ...(a.thumbId ? { thumbArtifactId: a.thumbId } : {}),
    ...(a.source ? { source: a.source } : {}),
    ...(a.collection ? { collection: a.collection } : {}),
    ...(a.external ? { external: a.external } : {}),
    ...(a.bundle ? { bundle: true } : {}),
  }
}

export function AutoArtifactGroup({
  items,
  access,
}: {
  items: readonly Artifact[]
  access: ArtifactViewerAccess
}) {
  const viewer = useMemo<ViewerContextValue>(
    () => ({
      connection: 'live',
      encryptedArtifacts: access.required,
      renderArtifact: (artifact, opts) => (
        <EndpointArtifact artifact={artifact} access={access} variant={opts?.variant} />
      ),
    }),
    [access],
  )
  const list = useMemo(() => items.map(toArtifactItem), [items])
  const versions = list.every((i) => i.external?.url && i.external.url === list[0]?.external?.url)
  return (
    <ViewerContext.Provider value={viewer}>
      <div className="grid gap-2">
        {versions && list.length > 1 ? (
          <ClaudeVersions items={list} />
        ) : (
          <ArtifactRun items={list} />
        )}
      </div>
    </ViewerContext.Provider>
  )
}

function ClaudeVersions({ items }: { items: readonly ArtifactItem[] }) {
  const sorted = [...items].sort(
    (a, b) => (b.external?.version ?? b.ts) - (a.external?.version ?? a.ts),
  )
  const [latest, ...older] = sorted
  if (!latest) return null
  return (
    <>
      <SingleArtifact item={latest} />
      <details className="grid gap-2">
        <summary className="cursor-pointer text-[14px] text-text-2">
          Earlier versions ({older.length.toLocaleString('en-US')})
        </summary>
        {older.map((it) => (
          <SingleArtifact key={it.id} item={it} />
        ))}
      </details>
    </>
  )
}
